import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Database,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import SmartResponseSubNav from './SmartResponseSubNav';
import {
  fetchSRExclusions,
  simulateSRLeadIntake,
  syncSRPoolToMeta,
} from '../../services/smartResponseApi';

export default function SmartResponseExclusionHub() {
  const { data, refetch } = useQuery({
    queryKey: ['sr-exclusions-all'],
    queryFn: () => fetchSRExclusions('all'),
  });

  const pools = data?.pools || [];
  const recentLeads = data?.recent_leads || [];

  const [syncResult, setSyncResult] = useState<Record<string, any>>({});

  // Live FeedOrder.aspx Dry-Run Validator state
  const [simCompKey, setSimCompKey] = useState<string>('no_onecall_rema1000_114927');
  const [simFirstName, setSimFirstName] = useState<string>('Kari');
  const [simLastName, setSimLastName] = useState<string>('Nordmann');
  const [simPhone, setSimPhone] = useState<string>('94455667');
  const [simAge, setSimAge] = useState<number>(38);
  const [simGender, setSimGender] = useState<'f' | 'm'>('f');
  const [simOperator, setSimOperator] = useState<string>('Telenor');
  const [simCompanyPhone, setSimCompanyPhone] = useState<string>('Nei (Privat mobil)');
  const [simVerdict, setSimVerdict] = useState<any>(null);

  const handleSyncPool = async (poolKey: string) => {
    const res = await syncSRPoolToMeta(poolKey, true);
    setSyncResult((prev) => ({ ...prev, [poolKey]: res }));
    refetch();
  };

  const handleRunValidator = async () => {
    const res = await simulateSRLeadIntake({
      comp_key: simCompKey,
      first_name: simFirstName,
      last_name: simLastName,
      phone: simPhone,
      age: simAge,
      gender: simGender,
      qualifying_answers: {
        current_operator: simOperator,
        company_phone: simCompanyPhone,
      },
      consent_given: true,
      persist_lead: false,
    });
    setSimVerdict(res);
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <SmartResponseSubNav />

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded bg-[#0F3F34] text-white">
                Annoncør-Dubletpuljer & FeedOrder.aspx Gate
              </span>
              <h2 className="text-xl font-bold text-gray-900 mt-1">
                Meta Custom Audience Eksklusioner (act_995004339585761) & Live FeedOrder Validator
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Verificeret med Lars hos SmartResponse: Dubletter tjekkes <strong>KUN per annoncør</strong> (fx{' '}
                <code>pool_no_onecall</code> 90 dage vs. <code>pool_no_talkmore</code> 45 dage). SmartResponse deler
                eksklusionslister direkte med vores Meta annoncekonto <code>act_995004339585761</code>.
              </p>
            </div>
          </div>
        </div>

        {/* Exclusion Pools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {pools.map((p) => {
            const synced = syncResult[p.pool_key];
            return (
              <div
                key={p.pool_key}
                className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="px-2 py-0.5 text-[11px] font-bold uppercase rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono">
                      {p.pool_key}
                    </span>
                    <span className="text-xs font-semibold text-gray-500">
                      Karenstid: {p.retention_days} dage
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 mb-3">{p.description}</p>

                  <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-gray-50 border border-gray-100 text-xs mb-3">
                    <div>
                      <p className="text-gray-500">Opsamlede Hashes</p>
                      <p className="text-sm font-extrabold text-gray-900">
                        {p.total_hashes.toLocaleString('da-DK')}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Meta Custom Audience</p>
                      <p className="text-xs font-bold font-mono text-[#0F3F34]">
                        {p.meta_custom_audience_id}
                      </p>
                    </div>
                  </div>

                  <div className="text-xs text-gray-600 mb-3">
                    <span className="font-semibold text-gray-700">Tilknyttede kampagner: </span>
                    {p.linked_competitions.map((ck) => (
                      <code
                        key={ck}
                        className="inline-block px-1.5 py-0.5 mr-1 mb-1 rounded bg-gray-100 text-gray-800 text-[10px]"
                      >
                        {ck}
                      </code>
                    ))}
                  </div>
                </div>

                <div>
                  <button
                    onClick={() => handleSyncPool(p.pool_key)}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#0F3F34] hover:opacity-95 text-white text-xs font-bold"
                  >
                    <RefreshCw size={13} />
                    Synkronisér Pulje til Meta ({p.meta_custom_audience_id})
                  </button>
                  {synced && (
                    <p className="text-[11px] text-emerald-700 font-semibold mt-2">
                      ✓ Synkroniseret mod {p.meta_custom_audience_id} på act_995004339585761
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FeedOrder.aspx Dry-Run Tester + Live Webhook Ledger */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Left: Live FeedOrder.aspx Dry-Run Validator */}
          <div className="lg:col-span-6 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-[#F07824]" />
              <h3 className="text-base font-bold text-gray-900">
                Live FeedOrder.aspx & Kvalificerings-Tester (Dry-Run)
              </h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Test nøjagtigt hvordan vores webhook validerer kvalificeringsspørgsmål (<code>7753</code>{' '}
              mobiloperatør, <code>8886</code> firmatelefon, alder 25–70 år) og genererer{' '}
              <code>FeedOrder.aspx</code> kaldet (inkl. <code>{'{tlf}@gmail.com'}</code> dummy-mail på OneCall) —{' '}
              <strong>uden</strong> at indsætte falske leads i dashboardet.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Konkurrence (`productId`)
                </label>
                <select
                  value={simCompKey}
                  onChange={(e) => setSimCompKey(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900"
                >
                  <option value="no_onecall_rema1000_114927">
                    [NO] OneCall: REMA 1000 (productId=114927 • 32 NOK)
                  </option>
                  <option value="no_talkmore_gogift_114903">
                    [NO] Talkmore: GoGift (productId=114903 • 40 NOK)
                  </option>
                  <option value="no_onecall_gogift_114889">
                    [NO] OneCall: GoGift (productId=114889 • 32 NOK)
                  </option>
                  <option value="no_onecall_ving_114874">
                    [NO] OneCall: VING (productId=114874 • 32 NOK)
                  </option>
                  <option value="dk_eforbruger_apollo_114919">
                    [DK] E-forbruger: Apollo (productId=114919 • 32 DKK)
                  </option>
                  <option value="dk_tryg_silvan_114888">
                    [DK] Tryg: Silvan (productId=114888 • 22 DKK)
                  </option>
                  <option value="dk_ase_apollo_114875">
                    [DK] ASE: Apollo (productId=114875 • 22 DKK)
                  </option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Fornavn (522)
                  </label>
                  <input
                    type="text"
                    value={simFirstName}
                    onChange={(e) => setSimFirstName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Efternavn (523)
                  </label>
                  <input
                    type="text"
                    value={simLastName}
                    onChange={(e) => setSimLastName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Mobilnr. (117)
                  </label>
                  <input
                    type="text"
                    value={simPhone}
                    onChange={(e) => setSimPhone(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Alder (119)
                  </label>
                  <input
                    type="number"
                    value={simAge}
                    onChange={(e) => setSimAge(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Køn (118)
                  </label>
                  <select
                    value={simGender}
                    onChange={(e) => setSimGender(e.target.value as 'f' | 'm')}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold"
                  >
                    <option value="f">Kvinde (241)</option>
                    <option value="m">Mand (240)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Operatør (7753)
                  </label>
                  <select
                    value={simOperator}
                    onChange={(e) => setSimOperator(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold"
                  >
                    <option value="Telenor">Telenor (812)</option>
                    <option value="Talkmore">Talkmore (6428)</option>
                    <option value="Ice">Ice (6430)</option>
                    <option value="Chili Mobil">Chili Mobil (10345)</option>
                    <option value="OneCall">OneCall (492 - Afvises på OneCall)</option>
                    <option value="Telia">Telia (342 - Afvises på OneCall)</option>
                    <option value="MyCall">MyCall (6427 - Afvises på OneCall)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Firmamobil (8886)
                  </label>
                  <select
                    value={simCompanyPhone}
                    onChange={(e) => setSimCompanyPhone(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold"
                  >
                    <option value="Nei (Privat mobil)">Nei (81 - Godkendt)</option>
                    <option value="Ja (Firmatelefon)">Ja (80 - Afvises)</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleRunValidator}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#F07824] hover:opacity-95 text-white text-xs font-bold shadow-sm"
              >
                <ShieldCheck size={15} />
                Test Kvalificering & Vis FeedOrder.aspx Payload (Dry-Run)
              </button>

              {simVerdict && (
                <div
                  className={`p-4 rounded-xl border text-xs space-y-2 ${
                    simVerdict.verdict?.passed
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                      : 'bg-red-50 border-red-300 text-red-900'
                  }`}
                >
                  <div className="flex items-center gap-2 font-bold text-sm">
                    {simVerdict.verdict?.passed ? (
                      <>
                        <CheckCircle2 size={18} className="text-emerald-600" />
                        <span>GODKENDT — Sendes til SmartResponse FeedOrder.aspx</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={18} className="text-red-600" />
                        <span>BLOKERET I PRE-FILTER — Sendes IKKE til SmartResponse</span>
                      </>
                    )}
                  </div>
                  <p>{simVerdict.verdict?.reason}</p>
                  {simVerdict.orderfeed?.orderfeed_payload && (
                    <pre className="p-2.5 rounded bg-gray-900 text-emerald-300 font-mono text-[11px] overflow-x-auto">
                      {JSON.stringify(simVerdict.orderfeed.orderfeed_payload, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Real-Time Webhook Lead Log */}
          <div className="lg:col-span-6 bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-[#0F3F34]" />
                  <h3 className="text-base font-bold text-gray-900">
                    Live Webhook & FeedOrder.aspx Transaktionslog
                  </h3>
                </div>
                <span className="text-xs text-gray-500">
                  Endpoint: <code>/api/smartresponse/webhooks/meta-leadgen</code>
                </span>
              </div>

              {recentLeads.length === 0 ? (
                <div className="p-6 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-center my-4">
                  <p className="text-sm font-bold text-gray-800">
                    Ingen live leads modtaget endnu (0 falske demo-leads)
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Så snart første deltager indsender Meta Instant Form på{' '}
                    <strong>PremiePortalen (1420182721178198)</strong>, logges leadet, kvalificeringssvarene og{' '}
                    <code>FeedOrder.aspx</code> status her i realtid.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500">
                        <th className="py-2 pr-2">Lead ID</th>
                        <th className="py-2 px-2">Kampagne</th>
                        <th className="py-2 px-2">Alder/Køn</th>
                        <th className="py-2 px-2">Status</th>
                        <th className="py-2 pl-2 text-right">Payout</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs">
                      {recentLeads.map((l) => (
                        <tr key={l.id}>
                          <td className="py-2 pr-2 font-mono text-[11px]">{l.leadgen_id}</td>
                          <td className="py-2 px-2 font-semibold">{l.comp_key}</td>
                          <td className="py-2 px-2">
                            {l.age_bucket} / {l.gender.toUpperCase()}
                          </td>
                          <td className="py-2 px-2">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                l.dedup_status === 'passed'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {l.sr_api_status}
                            </span>
                          </td>
                          <td className="py-2 pl-2 text-right font-bold">{l.payout_dkk} kr.</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-3.5 rounded-xl bg-[#F4F8F6] border border-emerald-200 text-xs text-gray-700 mt-4">
              <p className="font-bold text-[#0F3F34]">
                Sådan virker Meta-eksklusionsdelingen med SmartResponse:
              </p>
              <p className="mt-1">
                SmartResponse's interne afdeling deler deres løbende opdaterede eksklusionslister direkte med
                vores annoncekonto <code>act_995004339585761</code> i Meta. Når listen er delt i Ads Manager,
                opdateres den automatisk fra SmartResponse's ende og ekskluderes på alle vores Ad Sets.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
